/**
 * Per-provider display metadata.
 *
 * Two things live here and nothing else:
 *
 * - the **brand name**, which is never translated (it is a proper noun, and a
 *   localized "OpenAI" would not match the button the user is looking for);
 * - the **key-creation URL**, because not knowing where to get a key is the
 *   single biggest drop-off point in a BYOK flow.
 *
 * @module ai_services/components/ByokKeyManager/providerMeta
 */

import type { ByokProvider } from "../../api";

export interface ProviderMeta {
  /** Brand name. Untranslated by design. */
  name: string;
  /** Where the user creates a key for this provider. */
  keyUrl: string;
  /** Shown under the input so a paste-accident is caught before a round-trip. */
  keyHint: string;
}

export const PROVIDER_META: Record<ByokProvider, ProviderMeta> = {
  openai: {
    name: "OpenAI",
    keyUrl: "https://platform.openai.com/api-keys",
    keyHint: "sk-…",
  },
  anthropic: {
    name: "Anthropic",
    keyUrl: "https://console.anthropic.com/settings/keys",
    keyHint: "sk-ant-…",
  },
  gemini: {
    name: "Gemini",
    keyUrl: "https://aistudio.google.com/app/apikey",
    keyHint: "AIza…",
  },
  deepseek: {
    name: "DeepSeek",
    keyUrl: "https://platform.deepseek.com/api_keys",
    keyHint: "sk-…",
  },
  glm: {
    name: "GLM (Zhipu)",
    keyUrl: "https://open.bigmodel.cn/usercenter/apikeys",
    keyHint: "…",
  },
  kimi: {
    name: "Kimi (Moonshot)",
    keyUrl: "https://platform.moonshot.cn/console/api-keys",
    keyHint: "sk-…",
  },
  assemblyai: {
    name: "AssemblyAI",
    keyUrl: "https://www.assemblyai.com/app/api-keys",
    keyHint: "…",
  },
};

export function providerName(provider: string): string {
  return PROVIDER_META[provider as ByokProvider]?.name ?? provider;
}
